/**
 * Early clock-in window shared by server enforcement and settings UI.
 * Lives on `instance_role_presets.early_clock_in_minutes` with an optional
 * per-membership override (`instance_memberships.early_clock_in_minutes`).
 */
export const DEFAULT_EARLY_CLOCK_IN_MINUTES = 0;
export const MIN_EARLY_CLOCK_IN_MINUTES = 0;
export const MAX_EARLY_CLOCK_IN_MINUTES = 120;

/** Rounds to an integer; `null` when the input is not a finite number. */
export function parseEarlyClockInMinutes(value: unknown): number | null {
  if (value == null || value === "" || typeof value === "boolean") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
}

/** German validation message for settings forms; `null` when the value is in range. */
export function earlyClockInMinutesValidationError(value: unknown): string | null {
  const n = parseEarlyClockInMinutes(value);
  if (n === null) return "Bitte eine Zeit in Minuten eingeben.";
  if (n < MIN_EARLY_CLOCK_IN_MINUTES) {
    return `Die Vorlaufzeit muss mindestens ${MIN_EARLY_CLOCK_IN_MINUTES} Min. betragen.`;
  }
  if (n > MAX_EARLY_CLOCK_IN_MINUTES) {
    return `Die Vorlaufzeit darf maximal ${MAX_EARLY_CLOCK_IN_MINUTES} Min. betragen.`;
  }
  return null;
}

/** Rounds and clamps to the DB check constraint; `null` when not a finite number. */
export function clampEarlyClockInMinutes(value: unknown): number | null {
  const n = parseEarlyClockInMinutes(value);
  if (n === null) return null;
  return Math.min(
    MAX_EARLY_CLOCK_IN_MINUTES,
    Math.max(MIN_EARLY_CLOCK_IN_MINUTES, n),
  );
}

/** Membership override wins, then the preset value, then the product default. */
export function resolveEffectiveEarlyClockInMinutes(
  membershipMinutes: unknown,
  presetMinutes: unknown,
): number {
  return (
    clampEarlyClockInMinutes(membershipMinutes) ??
    clampEarlyClockInMinutes(presetMinutes) ??
    DEFAULT_EARLY_CLOCK_IN_MINUTES
  );
}
